/**
 * Pure helpers that summarize household data for the Home screen and the
 * AI plan-ready screen. No framework dependencies.
 *
 * Thresholds intentionally mirror the existing 3-tier cleanliness system
 * (see ProgressBar / getTaskStatus): < 60 % dirty = clean, 60–90 % = needs
 * attention, >= 90 % = time for action. A task counts as "due" at the same
 * >= 90 % threshold the daily allocator uses.
 */

export const NEEDS_ATTENTION_THRESHOLD = 60;
export const DUE_THRESHOLD = 90;

/** Rough effort estimate: one point of effort ≈ 5 minutes of cleaning. */
export const MINUTES_PER_POINT = 5;

export const isTaskDue = (dirtyPercent: number | undefined): boolean =>
  (dirtyPercent ?? 0) >= DUE_THRESHOLD;

/** True when both dates fall on the same calendar day in local time. */
export const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

interface CompletionLike {
  completed_at: string | null;
  deleted_at?: string | null;
}

/** Count completions finished today (local time), ignoring soft-deleted rows. */
export const countCompletedToday = (
  completions: readonly CompletionLike[],
  now: Date = new Date(),
): number =>
  completions.filter((c) => {
    if (!c.completed_at || c.deleted_at) return false;
    const done = new Date(c.completed_at);
    return !Number.isNaN(done.getTime()) && isSameLocalDay(done, now);
  }).length;

interface TaskLike {
  uuid: string;
  area_uuid: string | null;
}

/** Count tasks at or above the due threshold. */
export const countDueTasks = (
  tasks: readonly TaskLike[],
  cleanlinessMap: Record<string, number>,
): number => tasks.filter((t) => isTaskDue(cleanlinessMap[t.uuid])).length;

export interface AreaSummary {
  taskCount: number;
  dueCount: number;
  /** Average dirty percentage across the area's tasks (0 when no tasks). */
  avgDirty: number;
}

export const summarizeArea = (
  areaId: string,
  tasks: readonly TaskLike[],
  cleanlinessMap: Record<string, number>,
): AreaSummary => {
  const areaTasks = tasks.filter((t) => t.area_uuid === areaId);
  const taskCount = areaTasks.length;
  if (taskCount === 0) return { taskCount: 0, dueCount: 0, avgDirty: 0 };
  const total = areaTasks.reduce((sum, t) => sum + (cleanlinessMap[t.uuid] ?? 0), 0);
  return {
    taskCount,
    dueCount: areaTasks.filter((t) => isTaskDue(cleanlinessMap[t.uuid])).length,
    avgDirty: total / taskCount,
  };
};

export type CleanlinessTone = 'good' | 'warn' | 'bad';

export interface CleanlinessLevel {
  /** Number of filled segments in a 3-segment meter (3 = sparkling). */
  filled: 1 | 2 | 3;
  tone: CleanlinessTone;
  label: string;
}

/** Convert a dirty percentage into a 3-segment meter level. */
export const getCleanlinessLevel = (dirtyPercent: number): CleanlinessLevel => {
  if (dirtyPercent >= DUE_THRESHOLD) return { filled: 1, tone: 'bad', label: 'Time for action' };
  if (dirtyPercent >= NEEDS_ATTENTION_THRESHOLD) return { filled: 2, tone: 'warn', label: 'Needs attention' };
  return { filled: 3, tone: 'good', label: 'Shiny clean' };
};

/** "1 task", "3 tasks" */
export const pluralizeTasks = (count: number): string =>
  `${count} task${count === 1 ? '' : 's'}`;

/** Subtitle shown under a space/room name. */
export const describeAreaSummary = (summary: AreaSummary): string => {
  if (summary.taskCount === 0) return 'No tasks yet';
  if (summary.dueCount === 0) return 'All caught up';
  return `${pluralizeTasks(summary.dueCount)} due today`;
};

/** Estimated minutes for a set of tasks based on their effort points. */
export const estimateMinutes = (points: readonly number[]): number =>
  points.reduce((sum, p) => sum + Math.max(0, p), 0) * MINUTES_PER_POINT;
